<template>
  <div class="form-section">
    <h4 class="text-responsive-lg font-bold text-center mb-responsive text-ietextlight">
      Select Cancellation Reason
    </h4>
    
    <div class="p-4 border rounded-md bg-white shadow-sm mb-6">
      <div class="space-y-responsive">
        <!-- Fixed v-model prop binding -->
        <CancellationReasonSelector
          :model-value="reason"
          @update:model-value="updateReason"
          :error="errors?.reason"
        />
        
        <!-- Conditional Fields Based on Reason -->
        <div class="dynamic-content-container space-y-6" ref="dynamicContainerRef">
          <!-- Realtor Information Section (Only for selling home) -->
          <transition 
            name="fade" 
            @before-enter="onBeforeEnter" 
            @enter="onEnter" 
            @after-enter="onAfterEnter" 
            @before-leave="onBeforeLeave" 
            @leave="onLeave" 
            @after-leave="onAfterLeave"
          >
            <SellingHomeSection
              v-if="reason === 'selling_home'"
              :model-value="realtorInfo"
              @update:model-value="updateRealtorInfo"
              :errors="errors"
            />
          </transition>

          <!-- New Insurer Information -->
          <transition 
            name="fade" 
            @before-enter="onBeforeEnter" 
            @enter="onEnter" 
            @after-enter="onAfterEnter" 
            @before-leave="onBeforeLeave" 
            @leave="onLeave" 
            @after-leave="onAfterLeave"
          >
            <NewInsurerSection
              v-if="reason === 'new_insurer'"
              :model-value="dynamicFields.newInsurer"
              @update:model-value="(value) => updateDynamicField('newInsurer', value)"
              :today="today"
              :customer-type="customerType"
              :errors="errors"
            />
          </transition>

          <!-- Change in Ownership (Only for ownership change) -->
          <transition 
            name="fade" 
            @before-enter="onBeforeEnter" 
            @enter="onEnter" 
            @after-enter="onAfterEnter" 
            @before-leave="onBeforeLeave" 
            @leave="onLeave" 
            @after-leave="onAfterLeave"
          >
            <div 
              v-if="reason === 'ownership_change'" 
              data-section-name="ownership_change"
              v-memo="[reason === 'ownership_change', dynamicFields.ownershipChange]" 
              class="highlight-new p-4 bg-blue-50 rounded-lg border border-blue-200"
            >
              <h5 class="form-section-heading mb-4">Ownership Change Information</h5>
              
              <div class="form-grid-2 gap-4">
                <FormField
                  label="New Owner Name"
                  field-id="new-owner-name"
                  :error="errors?.newOwnerName"
                  required
                  class="md:col-span-2"
                >
                  <input
                    id="new-owner-name"
                    :value="dynamicFields.ownershipChange?.newOwnerName"
                    @input="updateDynamicField('ownershipChange', 'newOwnerName', $event.target.value)"
                    type="text"
                    class="input-responsive"
                    placeholder="Full name of new owner"
                  />
                </FormField>
                
                <FormField
                  label="Transfer Date"
                  field-id="transfer-date"
                  :error="errors?.transferDate"
                >
                  <ResponsiveDateInput
                    field-id="transfer-date"
                    :model-value="dynamicFields.ownershipChange?.transferDate"
                    @update:model-value="updateDynamicField('ownershipChange', 'transferDate', $event)"
                    :min-date="today"
                    :error="errors?.transferDate"
                    label=""
                    :required="false"
                    :showOptionalLabel="false"
                    :showRequiredLabel="false"
                    :userRole="props.customerType"
                  />
                </FormField>
                
                <FormField
                  label="Reason for Ownership Change"
                  field-id="ownership-change-reason"
                  :error="errors?.ownershipChangeReason"
                  required
                >
                  <select
                    id="ownership-change-reason"
                    :value="dynamicFields.ownershipChange?.reason"
                    @change="updateDynamicField('ownershipChange', 'reason', $event.target.value)"
                    class="form-select input-responsive"
                  >
                    <option value="" disabled>Select a reason</option>
                    <option value="sale">Property Sale</option>
                    <option value="gift">Gift/Transfer to Family</option>
                    <option value="inheritance">Inheritance</option>
                    <option value="legal">Legal Settlement</option>
                    <option value="other">Other</option>
                  </select>
                </FormField>
              </div>
            </div>
          </transition>

          <!-- Risk Reduction (Only for risk reduction) -->
          <transition 
            name="fade" 
            @before-enter="onBeforeEnter" 
            @enter="onEnter" 
            @after-enter="onAfterEnter" 
            @before-leave="onBeforeLeave" 
            @leave="onLeave" 
            @after-leave="onAfterLeave"
          >
            <div 
              v-if="reason === 'risk_reduction'" 
              data-section-name="risk_reduction"
              v-memo="[reason === 'risk_reduction', dynamicFields.riskReduction]" 
              class="highlight-new p-4 bg-blue-50 rounded-lg border border-blue-200"
            >
              <h5 class="form-section-heading mb-4">Risk Reduction Information</h5>
              
              <div class="space-y-4">
                <FormField
                  label="Description of Changes Made"
                  field-id="risk-reduction-description"
                  :error="errors?.riskReductionDescription"
                  required
                >
                  <textarea
                    id="risk-reduction-description"
                    :value="dynamicFields.riskReduction?.description"
                    @input="updateDynamicField('riskReduction', 'description', $event.target.value)"
                    class="input-responsive"
                    rows="3"
                    placeholder="Describe changes that have reduced the risk (e.g., security system installation, fire prevention)"
                  ></textarea>
                </FormField>
                
                <FormField
                  label="Date of Risk Reduction"
                  field-id="risk-reduction-date"
                  :error="errors?.riskReductionDate"
                >
                  <ResponsiveDateInput
                    field-id="risk-reduction-date"
                    :model-value="dynamicFields.riskReduction?.date"
                    @update:model-value="updateDynamicField('riskReduction', 'date', $event)"
                    :max-date="today"
                    :error="errors?.riskReductionDate"
                    label=""
                    :required="false"
                    :showOptionalLabel="false"
                    :showRequiredLabel="false"
                    :userRole="props.customerType"
                  />
                </FormField>
              </div>
            </div>
          </transition>

          <!-- Mortgage Change (Only for mortgage change) -->
          <transition 
            name="fade" 
            @before-enter="onBeforeEnter" 
            @enter="onEnter" 
            @after-enter="onAfterEnter" 
            @before-leave="onBeforeLeave" 
            @leave="onLeave" 
            @after-leave="onAfterLeave"
          >
            <div 
              v-if="reason === 'mortgage_change'" 
              data-section-name="mortgage_change"
              v-memo="[reason === 'mortgage_change', dynamicFields.mortgageChange]" 
              class="highlight-new p-4 bg-blue-50 rounded-lg border border-blue-200 mortgage-change-section"
            >
              <h5 class="form-section-heading mb-4">Mortgage Change Information</h5>
              
              <div class="form-grid-2 gap-4">
                <FormField
                  label="Previous Mortgage Company"
                  field-id="previous-mortgage-company"
                  :error="errors?.previousMortgageCompany"
                  required
                  class="md:col-span-2"
                >
                  <InputWithIcon 
                    :disabled="false" 
                    :userRole="props.customerType"
                    :hasError="!!errors?.previousMortgageCompany"
                  >
                    <template #default="{ hasIcon }">
                      <input
                        id="previous-mortgage-company"
                        :value="dynamicFields.mortgageChange?.previousCompany"
                        @input="updateDynamicField('mortgageChange', 'previousCompany', $event.target.value)"
                        type="text"
                        class="w-full h-full bg-transparent focus:outline-none focus:ring-0 focus:border-none"
                        :class="{ 
                          'rounded-l-none': hasIcon, 
                          'rounded-l-md': !hasIcon, 
                          'rounded-r-md': true 
                        }"
                        placeholder="Previous mortgage company name"
                      />
                    </template>
                  </InputWithIcon>
                </FormField>
                
                <FormField
                  label="Payoff Date"
                  field-id="mortgage-payoff-date"
                  :error="errors?.mortgagePayoffDate"
                >
                  <ResponsiveDateInput
                    field-id="mortgage-payoff-date"
                    :model-value="dynamicFields.mortgageChange?.payoffDate"
                    @update:model-value="updateDynamicField('mortgageChange', 'payoffDate', $event)"
                    :max-date="today"
                    label=""
                    :required="false"
                    :showOptionalLabel="false"
                    :showRequiredLabel="false"
                    :userRole="props.customerType"
                    :error="errors?.mortgagePayoffDate"
                  />
                </FormField>
                
                <FormField
                  label="Loan Number"
                  field-id="loan-number-container"
                  :error="errors?.loanNumber"
                  required
                >
                  <InputWithIcon 
                    :disabled="false" 
                    :userRole="props.customerType"
                    :hasError="!!errors?.loanNumber"
                  >
                    <template #default="{ hasIcon }">
                      <input
                        id="loan-number"
                        :value="dynamicFields.mortgageChange?.loanNumber"
                        @input="updateDynamicField('mortgageChange', 'loanNumber', $event.target.value)"
                        type="text"
                        class="w-full h-full bg-transparent focus:outline-none focus:ring-0 focus:border-none"
                        :class="{ 
                          'rounded-l-none': hasIcon, 
                          'rounded-l-md': !hasIcon, 
                          'rounded-r-md': true 
                        }"
                        placeholder="Loan number"
                      />
                    </template>
                  </InputWithIcon>
                </FormField>
              </div>
            </div>
          </transition>

          <!-- Moving (Only for moving) -->
          <transition 
            name="fade" 
            @before-enter="onBeforeEnter" 
            @enter="onEnter" 
            @after-enter="onAfterEnter" 
            @before-leave="onBeforeLeave" 
            @leave="onLeave" 
            @after-leave="onAfterLeave"
          >
            <div 
              v-if="reason === 'moving'" 
              data-section-name="moving"
              v-memo="[reason === 'moving', dynamicFields.moving]" 
              class="highlight-new p-4 bg-blue-50 rounded-lg border border-blue-200"
            >
              <h5 class="form-section-heading mb-4">Moving Information</h5>
              
              <div class="form-grid-2 gap-4">
                <FormField
                  label="New Address"
                  field-id="new-address"
                  :error="errors?.newAddress"
                  required
                  class="md:col-span-2"
                >
                  <input
                    id="new-address"
                    :value="dynamicFields.moving?.newAddress"
                    @input="updateDynamicField('moving', 'newAddress', $event.target.value)"
                    type="text"
                    class="input-responsive"
                    placeholder="New street address"
                  />
                </FormField>
                
                <FormField
                  label="Moving Date"
                  field-id="moving-date"
                  :error="errors?.movingDate"
                >
                  <ResponsiveDateInput
                    field-id="moving-date"
                    :model-value="dynamicFields.moving?.movingDate"
                    @update:model-value="updateDynamicField('moving', 'movingDate', $event)"
                    :min-date="today"
                    :error="errors?.movingDate"
                    label=""
                    :required="false"
                    :showOptionalLabel="false"
                    :showRequiredLabel="false"
                    :userRole="props.customerType"
                  />
                </FormField>
                
                <FormField
                  label="Is Property Sold?"
                  field-id="is-property-sold"
                  :error="errors?.isPropertySold"
                  required
                >
                  <div class="flex space-x-4">
                    <label class="inline-flex items-center">
                      <input
                        type="radio"
                        :checked="dynamicFields.moving?.isPropertySold === true"
                        @change="updateDynamicField('moving', 'isPropertySold', true)"
                        :class="props.customerType === 'customer' ? 'enhanced-checkbox-customer' : 'enhanced-checkbox'"
                      />
                      <span class="ml-2">Yes</span>
                    </label>
                    <label class="inline-flex items-center">
                      <input
                        type="radio"
                        :checked="dynamicFields.moving?.isPropertySold === false"
                        @change="updateDynamicField('moving', 'isPropertySold', false)"
                        :class="props.customerType === 'customer' ? 'enhanced-checkbox-customer' : 'enhanced-checkbox'"
                      />
                      <span class="ml-2">No</span>
                    </label>
                  </div>
                </FormField>
              </div>
            </div>
          </transition>

          <!-- Financial Hardship (Only for financial) -->
          <transition 
            name="fade" 
            @before-enter="onBeforeEnter" 
            @enter="onEnter" 
            @after-enter="onAfterEnter" 
            @before-leave="onBeforeLeave" 
            @leave="onLeave" 
            @after-leave="onAfterLeave"
          >
            <div 
              v-if="reason === 'financial'" 
              data-section-name="financial"
              v-memo="[reason === 'financial', dynamicFields.financial]" 
              class="highlight-new p-4 bg-blue-50 rounded-lg border border-blue-200"
            >
              <h5 class="form-section-heading mb-4">Financial Hardship Information</h5>
              
              <div class="form-grid-2 gap-4">
                <FormField
                  label="Hardship Type"
                  field-id="hardship-type"
                  :error="errors?.hardshipType"
                  required
                >
                  <select
                    id="hardship-type"
                    :value="dynamicFields.financial?.hardshipType"
                    @change="updateDynamicField('financial', 'hardshipType', $event.target.value)"
                    class="form-select input-responsive"
                  >
                    <option value="" disabled>Select type</option>
                    <option value="job_loss">Job Loss</option>
                    <option value="medical">Medical Expenses</option>
                    <option value="disaster">Natural Disaster</option>
                    <option value="divorce">Divorce/Separation</option>
                    <option value="business">Business Downturn</option>
                    <option value="other">Other Hardship</option>
                  </select>
                </FormField>
                
                <FormField
                  label="Expected Duration"
                  field-id="hardship-duration"
                  :error="errors?.hardshipDuration"
                  required
                >
                  <select
                    id="hardship-duration"
                    :value="dynamicFields.financial?.expectedDuration"
                    @change="updateDynamicField('financial', 'expectedDuration', $event.target.value)"
                    class="form-select input-responsive"
                  >
                    <option value="" disabled>Select duration</option>
                    <option value="temporary">Temporary (1-3 months)</option>
                    <option value="short_term">Short-term (3-6 months)</option>
                    <option value="long_term">Long-term (6+ months)</option>
                    <option value="permanent">Permanent change</option>
                    <option value="unknown">Unknown</option>
                  </select>
                </FormField>
                
                <FormField
                  label="Alternative Coverage Needs"
                  field-id="alternative-coverage"
                  :error="errors?.alternativeCoverage"
                  required
                  class="md:col-span-2"
                >
                  <textarea
                    id="alternative-coverage"
                    :value="dynamicFields.financial?.alternativeCoverage"
                    @input="updateDynamicField('financial', 'alternativeCoverage', $event.target.value)"
                    class="input-responsive"
                    rows="3"
                    placeholder="Describe any alternative coverage you might need"
                  ></textarea>
                </FormField>
              </div>
            </div>
          </transition>

          <!-- Consolidation of Policies (Only for consolidation) -->
          <transition 
            name="fade" 
            @before-enter="onBeforeEnter" 
            @enter="onEnter" 
            @after-enter="onAfterEnter" 
            @before-leave="onBeforeLeave" 
            @leave="onLeave" 
            @after-leave="onAfterLeave"
          >
            <div 
              v-if="reason === 'consolidation'" 
              data-section-name="consolidation"
              v-memo="[reason === 'consolidation', dynamicFields.consolidation]" 
              class="highlight-new p-4 bg-blue-50 rounded-lg border border-blue-200"
            >
              <h5 class="form-section-heading mb-4">Policy Consolidation Information</h5>
              
              <div class="form-grid-2 gap-4">
                <FormField
                  label="New Consolidated Policy Number"
                  field-id="consolidated-policy-number"
                  :error="errors?.consolidatedPolicyNumber"
                  required
                >
                  <input
                    id="consolidated-policy-number"
                    :value="dynamicFields.consolidation?.policyNumber"
                    @input="updateDynamicField('consolidation', 'policyNumber', $event.target.value)"
                    type="text"
                    class="input-responsive"
                    placeholder="New consolidated policy number"
                  />
                </FormField>
                
                <FormField
                  label="Insurance Provider"
                  field-id="insurance-provider"
                  :error="errors?.insuranceProvider"
                  required
                >
                  <input
                    id="insurance-provider"
                    :value="dynamicFields.consolidation?.provider"
                    @input="updateDynamicField('consolidation', 'provider', $event.target.value)"
                    type="text"
                    class="input-responsive"
                    placeholder="Insurance provider name"
                  />
                </FormField>
                
                <FormField
                  label="Effective Date"
                  field-id="consolidation-effective-date"
                  :error="errors?.consolidationEffectiveDate"
                  class="md:col-span-2"
                >
                  <ResponsiveDateInput
                    field-id="consolidation-effective-date"
                    :model-value="dynamicFields.consolidation?.effectiveDate"
                    @update:model-value="updateDynamicField('consolidation', 'effectiveDate', $event)"
                    :min-date="today"
                    :error="errors?.consolidationEffectiveDate"
                    label=""
                    :required="false"
                    :showOptionalLabel="false"
                    :showRequiredLabel="false"
                    :userRole="props.customerType"
                  />
                </FormField>
              </div>
            </div>
          </transition>

          <!-- Consultation Option -->
          <transition 
            name="fade" 
            @before-enter="onBeforeEnter" 
            @enter="onEnter" 
            @after-enter="onAfterEnter" 
            @before-leave="onBeforeLeave" 
            @leave="onLeave" 
            @after-leave="onAfterLeave"
          >
            <ConsultationSection
              v-if="showWinBackOption"
              :model-value="isWinBackSelected"
              @update:model-value="toggleWinBack"
              :callback-time="callbackTime"
              :today="today"
              :customer-type="customerType"
              :errors="errors"
              @update:callback-time="updateCallbackTime"
            />
          </transition>
        </div>
        
        <!-- Custom slot for additional fields -->
        <slot name="additional-fields"></slot>
      </div>
    </div>
    
    <NavigationButtons
      :customer-type="customerType"
      @previous="$emit('previous')"
      @next="handleNext"
    />
  </div>
</template>

<script setup>
import { computed, ref, nextTick, onMounted, watch } from 'vue';
import { Icon } from '@iconify/vue';
import FormField from '@/components/common/FormField.vue';
import ResponsiveDateInput from '@/components/common/ResponsiveDateInput.vue';
import InputWithIcon from '@/components/common/InputWithIcon.vue';
import { useTransitions } from './composables/useTransitions';
import { useCancellationForm } from './composables/useCancellationForm';

// Import components
import CancellationReasonSelector from './cancellation/CancellationReasonSelector.vue';
import SellingHomeSection from './cancellation/reasons/SellingHomeSection.vue';
import NewInsurerSection from './cancellation/reasons/NewInsurerSection.vue';
import ConsultationSection from './cancellation/ConsultationSection.vue';
import NavigationButtons from './cancellation/NavigationButtons.vue';

const props = defineProps({
  customerType: {
    type: String,
    default: 'customer',
    validator: value => ['customer', 'lender'].includes(value)
  },
  reason: {
    type: String,
    default: ''
  },
  realtorInfo: {
    type: Object,
    default: () => ({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: ''
    })
  },
  callbackTime: {
    type: Object,
    default: () => ({
      date: '',
      time: ''
    })
  },
  isWinBackSelected: {
    type: Boolean,
    default: false
  },
  dynamicFields: {
    type: Object,
    default: () => ({})
  },
  errors: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits([
  'update:reason',
  'update:realtorInfo',
  'update:callbackTime',
  'update:isWinBackSelected',
  'update:dynamicFields',
  'previous',
  'next',
  'skip-win-back'
]);

// Transitions
const { 
  onBeforeEnter, 
  onEnter, 
  onAfterEnter, 
  onBeforeLeave, 
  onLeave, 
  onAfterLeave 
} = useTransitions();

// Form Management
const {
  formData,
  errors: formErrors,
  isValid,
  validateForm,
  updateReason: updateFormReason,
  updateRealtorInfo: updateFormRealtorInfo,
  updateDynamicField: updateFormDynamicField,
  updateCallbackTime: updateFormCallbackTime,
  toggleWinBack: toggleFormWinBack,
  showWinBackOption
} = useCancellationForm({
  initialValues: {
    reason: props.reason,
    realtorInfo: props.realtorInfo,
    dynamicFields: props.dynamicFields,
    callbackTime: props.callbackTime,
    isWinBackSelected: props.isWinBackSelected
  }
});

// Sync form state with props
watch(() => props.reason, (newValue) => {
  updateFormReason(newValue);
});

watch(() => props.realtorInfo, (newValue) => {
  updateFormRealtorInfo(newValue);
}, { deep: true });

watch(() => props.dynamicFields, (newValue) => {
  Object.entries(newValue).forEach(([category, value]) => {
    updateFormDynamicField(category, value);
  });
}, { deep: true });

watch(() => props.callbackTime, (newValue) => {
  updateFormCallbackTime(newValue);
}, { deep: true });

watch(() => props.isWinBackSelected, (newValue) => {
  if (newValue !== formData.value.isWinBackSelected) {
    toggleFormWinBack();
  }
});

// Event handlers that emit updates to parent
const updateReason = (value) => {
  emit('update:reason', value);
};

const updateRealtorInfo = (value) => {
  emit('update:realtorInfo', value);
};

const updateDynamicField = (category, value) => {
  const updatedFields = { ...props.dynamicFields, [category]: value };
  emit('update:dynamicFields', updatedFields);
};

const updateCallbackTime = (value) => {
  emit('update:callbackTime', value);
};

const toggleWinBack = () => {
  emit('update:isWinBackSelected', !props.isWinBackSelected);
};

// Handle next button click with validation
const handleNext = async () => {
  const isValid = await validateForm();
  if (isValid) {
    emit('next');
  }
};

// Computed values
const today = computed(() => {
  return new Date().toISOString().split('T')[0];
});

// Performance logging (if needed)
const performanceLogging = ref(true);
const renderTimes = ref({});
const startTime = ref(null);
const reasonChangeTime = ref(null);
const dynamicContainerRef = ref(null);

onMounted(() => {
  if (performanceLogging.value) {
    console.log('SharedCancellationReasonStep mounted');
  }
});
</script>

<style scoped>
.form-section {
  @apply p-4 md:p-6;
}

.form-section-heading {
  @apply text-lg font-semibold text-gray-800 mb-4;
}

.form-grid-2 {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.flex-responsive-col {
  @apply flex flex-col sm:flex-row sm:justify-between;
}

.btn-responsive-secondary {
  @apply px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition-colors;
}

.input-responsive {
  @apply w-full px-3 py-2 text-base sm:text-sm;
  @apply h-10; /* Standard height */
  @apply border border-gray-300 rounded-md shadow-sm;
  @apply bg-white;
  @apply focus:outline-none focus:ring-2;
}

/* Remove specific focus styles here - they're now handled by the components */
:deep(.input-responsive) {
  @apply focus:outline-none;
}

textarea.input-responsive {
  @apply h-auto;
  min-height: theme('spacing.20');
}

/* Custom styling for select elements to achieve uniformity */
select.input-responsive {
  @apply appearance-none; /* Remove default browser styling */
  @apply pr-8; /* Add padding to the right for the custom arrow */
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e"); /* Tailwind's default gray-500 chevron down */
  background-repeat: no-repeat;
  background-position: right 0.5rem center; /* Position arrow */
  background-size: 1.25em 1.25em; /* Size of the arrow */
}

.space-y-responsive {
  @apply space-y-4 md:space-y-6;
  position: relative;
}

.mb-responsive {
  @apply mb-4 md:mb-6;
}

.mt-responsive {
  @apply mt-4 md:mt-6;
}

.text-responsive-lg {
  @apply text-lg md:text-xl;
}

.mobile-enhanced-button {
  @apply py-3 px-4 text-base sm:py-2 sm:px-3 sm:text-sm;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  /* Height is animated based on JS values, CSS provides duration/easing */
  /* Opacity and Transform are also handled here */
  transition: opacity 0.2s ease, transform 0.2s ease, height 0.3s ease !important; /* Ensure height transition is picked up */
  overflow: visible !important; /* Prevent content clipping during transitions */
  min-height: 0; /* Ensure height animations work correctly */
}

/* .fade-leave-active { */
  /* z-index: 0; /* Not needed */
/* } */

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(5px); /* Keep the subtle movement */
  /* Height will be set to 0px by JS for enter-from and leave-to states */
}

.highlight-new {
  /* This is the element (el) in the JS hooks */
  contain: layout; /* Can still be beneficial */
  width: 100%;
  box-sizing: border-box; /* Good practice */
  overflow: visible !important; /* Ensure content doesn't get clipped */
  position: relative; /* Ensure proper stacking context */
  z-index: 1; /* Provide a stacking context */
}

/* ... rest of existing styles ... */

.select-with-custom-arrow {
  @apply appearance-none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1.25em 1.25em;
}

/* New insurer section specific handling */
[data-section-name="new_insurer"] {
  z-index: 2; /* Ensure it sits above other sections */
  position: relative;
}

/* Consultation section handling */
[data-section-name="consultation_option"] {
  z-index: 3; /* Ensure it sits above other sections including new_insurer */
  position: relative;
  margin-top: 1rem;
  clear: both; /* Ensure it starts on a new line */
}

.consultation-section {
  margin-top: 2rem !important;
  border-top: 1px solid #e5e7eb;
  padding-top: 1.5rem;
}

.dynamic-content-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
</style> 